import {
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent
} from 'react';
import { Check, ChevronDown, Search, X } from 'lucide-react';

export interface SearchableSelectOption {
  value: string;
  label: string;
  searchText?: string;
  description?: string;
}

interface SearchableSelectProps {
  value: string;
  options: SearchableSelectOption[];
  onChange: (value: string) => void;
  placeholder: string;
  searchPlaceholder?: string;
  emptyText?: string;
  noResultsText?: string;
  allowClear?: boolean;
  disabled?: boolean;
  className?: string;
  ariaLabel?: string;
}

function normalized(value: string): string {
  return value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLocaleLowerCase('hr-HR')
    .trim();
}

export function SearchableSelect({
  value,
  options,
  onChange,
  placeholder,
  searchPlaceholder = 'Upišite za pretragu…',
  emptyText = 'Nema dostupnih stavki.',
  noResultsText = 'Nema rezultata za upisani pojam.',
  allowClear = false,
  disabled = false,
  className = '',
  ariaLabel
}: SearchableSelectProps) {
  const rootRef = useRef<HTMLDivElement>(null);
  const listboxId = useId();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [activeIndex, setActiveIndex] = useState(0);
  const selected = options.find((option) => option.value === value);

  const visibleOptions = useMemo(() => {
    const needle = normalized(query);
    if (!needle) return options;
    return options.filter((option) =>
      normalized(`${option.label} ${option.searchText ?? ''}`).includes(needle)
    );
  }, [options, query]);

  useEffect(() => {
    function closeOnOutside(event: PointerEvent) {
      if (!rootRef.current?.contains(event.target as Node)) {
        setOpen(false);
        setQuery('');
      }
    }
    document.addEventListener('pointerdown', closeOnOutside);
    return () => document.removeEventListener('pointerdown', closeOnOutside);
  }, []);

  useEffect(() => setActiveIndex(0), [query, open]);

  function choose(nextValue: string) {
    onChange(nextValue);
    setOpen(false);
    setQuery('');
  }

  function handleKeyDown(event: KeyboardEvent<HTMLInputElement>) {
    if (event.key === 'Escape') {
      setOpen(false);
      setQuery('');
      return;
    }
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setOpen(true);
      setActiveIndex((index) => Math.min(index + 1, visibleOptions.length - 1));
      return;
    }
    if (event.key === 'ArrowUp') {
      event.preventDefault();
      setActiveIndex((index) => Math.max(index - 1, 0));
      return;
    }
    if (event.key === 'Enter' && open && visibleOptions[activeIndex]) {
      event.preventDefault();
      choose(visibleOptions[activeIndex].value);
    }
  }

  return (
    <div className={`searchable-select ${open ? 'is-open' : ''} ${className}`} ref={rootRef}>
      <div className="searchable-select__control">
        <Search aria-hidden="true" />
        <input
          value={open ? query : selected?.label ?? ''}
          placeholder={open ? searchPlaceholder : placeholder}
          aria-label={ariaLabel ?? placeholder}
          aria-controls={listboxId}
          aria-expanded={open}
          aria-autocomplete="list"
          role="combobox"
          disabled={disabled}
          autoComplete="off"
          onFocus={() => {
            setOpen(true);
            setQuery('');
          }}
          onClick={() => setOpen(true)}
          onChange={(event) => {
            setQuery(event.target.value);
            setOpen(true);
          }}
          onKeyDown={handleKeyDown}
        />
        {allowClear && value ? (
          <button type="button" className="searchable-select__clear" onClick={() => choose('')} aria-label="Poništi odabir">
            <X />
          </button>
        ) : (
          <ChevronDown className="searchable-select__chevron" aria-hidden="true" />
        )}
      </div>

      {open && (
        <div className="searchable-select__menu" id={listboxId} role="listbox">
          {allowClear && !query && (
            <button type="button" role="option" aria-selected={!value} onClick={() => choose('')}>
              <span><strong>{placeholder}</strong></span>
              {!value && <Check />}
            </button>
          )}
          {visibleOptions.map((option, index) => (
            <button
              type="button"
              role="option"
              aria-selected={option.value === value}
              className={index === activeIndex ? 'is-active' : ''}
              key={option.value}
              onMouseEnter={() => setActiveIndex(index)}
              onClick={() => choose(option.value)}
            >
              <span>
                <strong>{option.label}</strong>
                {option.description && <small>{option.description}</small>}
              </span>
              {option.value === value && <Check />}
            </button>
          ))}
          {!options.length && <p>{emptyText}</p>}
          {options.length > 0 && !visibleOptions.length && <p>{noResultsText}</p>}
        </div>
      )}
    </div>
  );
}
